/**
 * Firebase — web entry point.
 * Webpack aliases app/services/Firebase to this file (see webpack.config.js).
 * Exports the same API as Firebase.ts using the Firebase JS SDK directly.
 */
import Constants from 'expo-constants';
import { getAnalytics, logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { getApp, getApps, initializeApp } from 'firebase/app';
import {
  getAuth,
  isSignInWithEmailLink as _isSignInWithEmailLink,
  onAuthStateChanged,
  signInWithCustomToken,
  signInWithEmailLink,
  signOut,
} from 'firebase/auth';
import { fetchAndActivate, getAll, getRemoteConfig, getValue } from 'firebase/remote-config';

if (getApps().length === 0) {
  const config = Constants.expoConfig?.web?.config?.firebase;
  if (!config) throw new Error('missing required firebase web config');
  initializeApp(config);
}

// JS SDK returns boolean synchronously; RNFirebase v23 returns Promise<boolean>.
// Wrap to keep the interface consistent for callers that await.
function isSignInWithEmailLink(auth: ReturnType<typeof getAuth>, url: string): Promise<boolean> {
  return Promise.resolve(_isSignInWithEmailLink(auth, url));
}

// JS SDK doesn't export setConfigSettings — settings are assigned directly on the instance.
function setConfigSettings(
  remoteConfig: ReturnType<typeof getRemoteConfig>,
  settings: Partial<{ minimumFetchIntervalMillis: number; fetchTimeMillis: number }>
): Promise<void> {
  Object.assign(remoteConfig.settings, settings);
  return Promise.resolve();
}

export {
  getApp,
  getApps,
  initializeApp,
  getAuth,
  onAuthStateChanged,
  signInWithCustomToken,
  signInWithEmailLink,
  isSignInWithEmailLink,
  signOut,
  getAnalytics,
  logEvent,
  setUserId,
  setUserProperties,
  getRemoteConfig,
  fetchAndActivate,
  getAll,
  getValue,
  setConfigSettings,
};

const crashlyticsStub = {
  log: () => {},
  recordError: () => {},
  setUserId: () => Promise.resolve(null),
  setAttribute: () => Promise.resolve(null),
  setAttributes: () => Promise.resolve(null),
  crash: () => {},
  setCrashlyticsCollectionEnabled: () => Promise.resolve(null),
};

export const getCrashlytics = () => crashlyticsStub;
